import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMediaQuery } from 'react-responsive';
import { DbAppSettings } from '@harvestiq/iiq-common';
import { mergeOrgAppSettings } from 'common';
import { defaultDbAppSettings } from '@harvestiq/iiq-common/app-settings/default-db-app-settings';
import BaseLoading from '../../components/Loading/BaseLoading';
import { isMobile as isMobileDevice } from 'react-device-detect';

type SettingsResponse = {
  appSettings: DbAppSettings;
  roles: string[]; // TODO: make RoleIds shared
  [key: string]: any;
};

export type AppSettingsContextType = {
  appSettings: DbAppSettings;
  isLoaded: boolean;
  isMobile: boolean;
  refetch: () => Promise<any>;
  orgAppSettingsDefaults: Record<string, any>;
  mergeOrgAppSettings: (settings: Record<string, any>) => Record<string, any>;
  [key: string]: any;
};

export const AppSettingsContext = createContext<
  AppSettingsContextType | undefined
>(undefined);

const useAppSettingsProvider = (): AppSettingsContextType => {
  const queryKey = 'getSettings';

  const {
    data: {
      appSettings = defaultDbAppSettings,
      ...restSettings
    } = {} as SettingsResponse,
    refetch,
    isLoading,
  } = useQuery<SettingsResponse>({
    queryKey: [queryKey],
    queryFn: async () => {
      // NOTE: This request made with no dependency on the authenticated API context so that it can sit at top of the provider tree
      const response = await fetch('/api/v1/settings');

      if (!response.ok) {
        console.error('Unexpected error when fetching settings.');
        return {} as SettingsResponse;
      }

      const { data: settings } = (await response.json()) as {
        data: SettingsResponse;
      };

      return settings;
    },
    staleTime: Infinity,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isMobile =
    isMobileDevice ?? useMediaQuery({ query: '(max-width: 770px)' });

  return {
    appSettings: mergeOrgAppSettings(appSettings) as unknown as DbAppSettings,
    isLoaded: !isLoading,
    isMobile,
    refetch,
    orgAppSettingsDefaults: defaultDbAppSettings,
    mergeOrgAppSettings,
    ...restSettings,
  };
};

type AppSettingsProviderProps = {
  children: ReactNode;
};

export const AppSettingsProvider = ({ children }: AppSettingsProviderProps) => {
  const appSettings = useAppSettingsProvider();

  return (
    <AppSettingsContext.Provider value={appSettings}>
      {appSettings.isLoaded ? children : <BaseLoading />}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = (): AppSettingsContextType => {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error(
      'useAppSettings must be used within an AppSettingsProvider'
    );
  }
  return context;
};
